import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Save, Plus, Trash } from 'lucide-react';
import { BusinessLandingSettings, BusinessFeature, defaultLandingSettings } from '@/types/businessLanding';
import { getBusinessLandingSettings, saveBusinessLandingSettings } from '@/utils/landingService';

const featureSchema = z.object({
  id: z.string(),
  title: z.string().min(1, "Title is required"),
  description: z.string().min(1, "Description is required"),
  iconName: z.string().optional()
});

const landingSettingsSchema = z.object({
  primaryColor: z.string().regex(/^#([A-Fa-f0-9]{6})$/, "Must be a valid hex color"),
  secondaryColor: z.string().regex(/^#([A-Fa-f0-9]{6})$/, "Must be a valid hex color"),
  accentColor: z.string().regex(/^#([A-Fa-f0-9]{6})$/, "Must be a valid hex color"),
  backgroundColor: z.string().regex(/^#([A-Fa-f0-9]{6})$/, "Must be a valid hex color"),
  textColor: z.string().regex(/^#([A-Fa-f0-9]{6})$/, "Must be a valid hex color"),
  heroTitle: z.string().min(1, "Hero title is required"),
  heroSubtitle: z.string().min(1, "Hero subtitle is required"),
  featureTitle: z.string().min(1, "Feature section title is required"),
  features: z.array(featureSchema).min(1, "At least one feature is required"),
  ctaTitle: z.string().min(1, "CTA title is required"),
  ctaText: z.string().min(1, "CTA text is required"),
  ctaButtonText: z.string().min(1, "Button text is required"),
  footerText: z.string().min(1, "Footer text is required"),
  metaTitle: z.string().min(1, "Meta title is required"),
  metaDescription: z.string().min(1, "Meta description is required")
});

const LandingPageEditor: React.FC<{
  businessId: string;
  onSaved?: () => void;
}> = ({ businessId, onSaved }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  
  const form = useForm<z.infer<typeof landingSettingsSchema>>({
    resolver: zodResolver(landingSettingsSchema),
    defaultValues: {
      ...defaultLandingSettings,
      features: defaultLandingSettings.features
    }
  });

  useEffect(() => {
    const loadSettings = async () => {
      setIsLoading(true);
      try {
        const settings = await getBusinessLandingSettings(businessId);
        
        // Update form values
        Object.keys(settings).forEach(key => {
          if (key !== 'businessId' && key !== 'id' && key !== 'features') {
            // @ts-ignore
            form.setValue(key, settings[key]);
          }
        });
        
        // Set features separately
        form.setValue('features', settings.features);
      } catch (error) {
        console.error('Error loading landing page settings:', error);
        toast({
          title: 'Error',
          description: 'Failed to load landing page settings',
          variant: 'destructive'
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    loadSettings();
  }, [businessId, form, toast]);

  const onSubmit = async (data: z.infer<typeof landingSettingsSchema>) => {
    setIsSubmitting(true);
    
    try {
      const settings: BusinessLandingSettings = {
        businessId,
        primaryColor: data.primaryColor,
        secondaryColor: data.secondaryColor,
        accentColor: data.accentColor,
        backgroundColor: data.backgroundColor,
        textColor: data.textColor,
        heroTitle: data.heroTitle,
        heroSubtitle: data.heroSubtitle,
        featureTitle: data.featureTitle,
        features: data.features.map(feature => ({
          id: feature.id,
          title: feature.title,
          description: feature.description,
          iconName: feature.iconName
        })),
        ctaTitle: data.ctaTitle,
        ctaText: data.ctaText,
        ctaButtonText: data.ctaButtonText,
        footerText: data.footerText,
        metaTitle: data.metaTitle,
        metaDescription: data.metaDescription
      };
      
      await saveBusinessLandingSettings(settings);
      
      toast({
        title: 'Success',
        description: 'Landing page settings saved successfully'
      });
      
      if (onSaved) {
        onSaved();
      }
    } catch (error) {
      console.error('Error saving landing page settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to save landing page settings',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const addFeature = () => {
    const currentFeatures = form.getValues('features') || [];
    const newFeature: BusinessFeature = {
      id: String(Date.now()),
      title: '',
      description: '',
      iconName: 'star'
    };
    
    form.setValue('features', [...currentFeatures, newFeature]);
  };

  const removeFeature = (index: number) => {
    const currentFeatures = form.getValues('features');
    const updatedFeatures = [...currentFeatures];
    updatedFeatures.splice(index, 1);
    form.setValue('features', updatedFeatures);
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex justify-center items-center p-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary-blue" />
            <span className="ml-2">Loading landing page settings...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Landing Page Customization</CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Colors</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="primaryColor"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Primary Color</FormLabel>
                      <div className="flex items-center space-x-2">
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <div 
                          className="h-10 w-10 rounded-md border"
                          style={{ backgroundColor: field.value }}
                        />
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="secondaryColor"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Secondary Color</FormLabel>
                      <div className="flex items-center space-x-2">
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <div 
                          className="h-10 w-10 rounded-md border"
                          style={{ backgroundColor: field.value }}
                        />
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="accentColor"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Accent Color</FormLabel>
                      <div className="flex items-center space-x-2">
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <div 
                          className="h-10 w-10 rounded-md border"
                          style={{ backgroundColor: field.value }}
                        />
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="backgroundColor"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Background Color</FormLabel>
                      <div className="flex items-center space-x-2">
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <div 
                          className="h-10 w-10 rounded-md border"
                          style={{ backgroundColor: field.value }}
                        />
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="textColor"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Text Color</FormLabel>
                      <div className="flex items-center space-x-2">
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <div 
                          className="h-10 w-10 rounded-md border"
                          style={{ backgroundColor: field.value }}
                        />
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
            
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Hero Section</h3>
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="heroTitle"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Hero Title</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="heroSubtitle"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Hero Subtitle</FormLabel>
                      <FormControl>
                        <Textarea {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
            
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Features Section</h3>
              <FormField
                control={form.control}
                name="featureTitle"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Features Title</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <div className="space-y-4">
                {form.watch('features')?.map((feature, index) => (
                  <Card key={feature.id} className="p-4">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-medium">Feature {index + 1}</h4>
                      <Button 
                        type="button" 
                        variant="destructive" 
                        size="sm"
                        onClick={() => removeFeature(index)}
                      >
                        <Trash className="h-4 w-4" />
                      </Button>
                    </div>
                    
                    <div className="space-y-4">
                      <FormField
                        control={form.control}
                        name={`features.${index}.title`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Title</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name={`features.${index}.description`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Description</FormLabel>
                            <FormControl>
                              <Textarea {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name={`features.${index}.iconName`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Icon Name</FormLabel>
                            <FormControl>
                              <Input {...field} placeholder="e.g., star, gift, users" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </Card>
                ))}
                
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={addFeature}
                  className="w-full"
                >
                  <Plus className="h-4 w-4 mr-2" /> Add Feature
                </Button>
              </div>
            </div>
            
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Call to Action</h3>
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="ctaTitle"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>CTA Title</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="ctaText"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>CTA Text</FormLabel>
                      <FormControl>
                        <Textarea {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="ctaButtonText"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Button Text</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
            
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Footer</h3>
              <FormField
                control={form.control}
                name="footerText"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Footer Copyright Text</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">SEO</h3>
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="metaTitle"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Meta Title</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="metaDescription"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Meta Description</FormLabel>
                      <FormControl>
                        <Textarea {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
            
            <Button 
              type="submit" 
              disabled={isSubmitting}
              className="w-full"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" /> Save Landing Page Settings
                </>
              )}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default LandingPageEditor;
